import { ArrowLeftRight, Check, ChevronDown, Scale, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AppShell } from "../components/app-shell";
import { Disclaimer } from "../components/disclaimer";
import { ProductCard } from "../components/product-card";
import { products } from "../lib/catalog";
import { matchProducts } from "../lib/matching";
import { loadProject, loadVariants } from "../lib/storage";

const formatEuro = (cents: number) =>
  new Intl.NumberFormat("hr-HR", { style: "currency", currency: "EUR" }).format(cents / 100);

export function DetailPage() {
  const { id = "", variantId = "" } = useParams();
  const project = useMemo(() => loadProject(id), [id]);
  const variants = useMemo(() => loadVariants(id), [id]);
  const variant = variants.find((item) => item.id === variantId);
  const variantNumber = Math.max(1, variants.findIndex((item) => item.id === variantId) + 1);
  const [showOriginal, setShowOriginal] = useState(false);
  const [selectedElement, setSelectedElement] = useState(0);
  const [sort, setSort] = useState("match");

  if (!project || !variant) {
    return (
      <AppShell eyebrow="Prijedlog nije pronađen">
        <div className="empty-state">
          <img src="/images/empty-state-480.webp" width="480" height="480" alt="" />
          <h1>Ovaj prijedlog više nije dostupan.</h1>
          <Link className="button button--primary" to="/app/new">
            Pokreni novi projekt
          </Link>
        </div>
      </AppShell>
    );
  }

  const element = variant.detectedElements[selectedElement];
  let matches = matchProducts(element, products, project.preferences.budgetMax * 100);
  if (sort === "price") matches = [...matches].sort((a, b) => a.product.priceCents - b.product.priceCents);
  if (sort === "size") {
    matches = [...matches].sort(
      (a, b) =>
        Math.abs((a.product.widthCm ?? 0) - (element.widthCm ?? 0)) -
        Math.abs((b.product.widthCm ?? 0) - (element.widthCm ?? 0)),
    );
  }

  return (
    <AppShell
      eyebrow={`Prijedlog ${variantNumber} · ${project.space}`}
      backTo={`/app/projects/${id}/results`}
      backLabel="Svih pet prijedloga"
    >
      <section className="detail-hero">
        <div className="detail-hero__copy">
          <p className="section-label">{variant.style}</p>
          <h1>{variant.title}</h1>
          <p>{variant.explanation}</p>
          <dl className="detail-stats">
            <div>
              <dt>Okvirni trošak</dt>
              <dd>
                {formatEuro(variant.estimatedMinCents)}–{formatEuro(variant.estimatedMaxCents)}
              </dd>
            </div>
            <div>
              <dt>Detektirano</dt>
              <dd>{variant.detectedElements.length} ključna elementa</dd>
            </div>
          </dl>
        </div>
        <div className="detail-hero__visual">
          <img
            src={
              showOriginal
                ? (project.originalImageUrl ?? "/images/interior-before-1024.webp")
                : variant.imageUrl
            }
            alt={
              showOriginal ? `Originalna fotografija: ${project.space}` : `${variant.title}, AI vizualizacija`
            }
            width="1536"
            height="1024"
          />
          <div className="image-toggle" role="group" aria-label="Prikaz fotografije">
            <button
              type="button"
              className={showOriginal ? "is-active" : ""}
              onClick={() => setShowOriginal(true)}
            >
              Original
            </button>
            <button
              type="button"
              className={!showOriginal ? "is-active" : ""}
              onClick={() => setShowOriginal(false)}
            >
              Rezultat
            </button>
            <ArrowLeftRight aria-hidden="true" />
          </div>
          {!showOriginal &&
            variant.detectedElements.slice(0, 3).map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={`element-pin element-pin--${index + 1}`}
                aria-label={`Prikaži proizvode za ${item.label}`}
                onClick={() => setSelectedElement(index)}
              >
                {index + 1}
              </button>
            ))}
        </div>
      </section>

      <Disclaimer />

      <section className="matching-section" aria-labelledby="matching-title">
        <aside className="element-panel">
          <p className="section-label">Prepoznati elementi</p>
          <h2 id="matching-title">Što smo pronašli u prijedlogu</h2>
          <div className="element-list" role="list">
            {variant.detectedElements.map((item, index) => (
              <button
                type="button"
                role="listitem"
                key={item.id}
                className={selectedElement === index ? "is-active" : ""}
                onClick={() => setSelectedElement(index)}
              >
                <span>{index + 1}</span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.colourTags.concat(item.materialTags).join(" · ")}</small>
                </div>
                {selectedElement === index && <Check aria-hidden="true" />}
              </button>
            ))}
          </div>
          <div className="element-spec">
            <Scale aria-hidden="true" />
            <div>
              <strong>Okvir za matching</strong>
              <p>
                {element.widthCm ? `Najviše ${element.widthCm} cm širine. ` : ""}
                Budžet projekta do {project.preferences.budgetMax.toLocaleString("hr-HR")} €.
              </p>
            </div>
          </div>
        </aside>
        <div className="product-results">
          <div className="product-results__heading">
            <div>
              <p className="section-label">{matches.length} sličnih demo proizvoda</p>
              <h2>{element.label}</h2>
              <p>Rangirano prema kategoriji, boji, stilu, mjeri, budžetu, značajkama i materijalu.</p>
            </div>
            <label className="sort-field">
              <SlidersHorizontal aria-hidden="true" />
              <span className="sr-only">Sortiraj proizvode</span>
              <select value={sort} onChange={(event) => setSort(event.target.value)}>
                <option value="match">Najbolji match</option>
                <option value="price">Najniža cijena</option>
                <option value="size">Najbliža dimenzija</option>
              </select>
              <ChevronDown aria-hidden="true" />
            </label>
          </div>
          {matches.length ? (
            <>
              <div className="product-grid">
                {matches.slice(0, 6).map((match) => (
                  <ProductCard key={match.product.id} match={match} />
                ))}
              </div>
              <div className="comparison-wrap">
                <h3>Brza usporedba</h3>
                <div className="comparison-scroll" tabIndex={0} aria-label="Usporedna tablica proizvoda">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Proizvod</th>
                        <th scope="col">Trgovina</th>
                        <th scope="col">Cijena</th>
                        <th scope="col">Širina</th>
                        <th scope="col">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.slice(0, 4).map((match) => (
                        <tr key={match.product.id}>
                          <th scope="row">{match.product.title}</th>
                          <td>{match.product.store}</td>
                          <td>{formatEuro(match.product.priceCents)}</td>
                          <td>{match.product.widthCm ? `${match.product.widthCm} cm` : "—"}</td>
                          <td>{match.level}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="no-results">
              <h3>Nema proizvoda za ovaj element.</h3>
              <p>Pokušaj s većim budžetom ili bez zadane mjere.</p>
            </div>
          )}
        </div>
      </section>
    </AppShell>
  );
}
